import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Users, DollarSign, Calendar, CheckCircle, Video, Mail, Phone, MapPin, Facebook, Twitter, Linkedin } from 'lucide-react';

import { GOOGLE_CALENDAR_BOOKING_URL } from '../../constants/appStore';

export default function Landing() {
  // Screenshots slideshow state
  const screenshots = [
    "/app-screenshots/login.jpg",
    "/app-screenshots/notes.jpg", 
    "/app-screenshots/messages.jpg",
    "/app-screenshots/feed.jpg",
    "/app-screenshots/calander.jpg",
    "/app-screenshots/sidebar.jpg",
    "/app-screenshots/create-class-1.jpg",
    "/app-screenshots/create-class-2.jpg",
    "/app-screenshots/create-class-3.jpg",
  ];
  
  const [currentScreenshotIndex, setCurrentScreenshotIndex] = useState(0);

  // Auto-rotate screenshots every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentScreenshotIndex((prevIndex) => 
        (prevIndex + 1) % screenshots.length
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [screenshots.length]);

  return (
    <>
      <Helmet>
        <title>RollOps - Gym Management System</title>
        <meta name="description" content="Manage your gym, members, and subscriptions all in one place with RollOps." />
      </Helmet>
      
      <main className="pt-16 min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter']">
        {/* Hero Section - Video background with vignette */}
        <section className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center overflow-hidden bg-[#111010]">
          {/* Video background */}
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src="/rollops-landing-page-video.mp4" type="video/mp4" />
          </video>
          {/* Vignette overlay - darkens edges */}
          <div
            className="absolute inset-0"
            style={{
              background: `
                radial-gradient(ellipse 60% 60% at 50% 50%, transparent 20%, rgba(17, 16, 16, 0.55) 55%, rgba(17, 16, 16, 0.95) 100%)
              `,
            }}
          />
          {/* Content - above video and vignette */}
          <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-center">
            <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-4 md:mb-6">
              Roll<span className="text-rollops-accent">O</span>ps
            </h1>
            <p className="text-lg md:text-xl lg:text-2xl text-[#A9A3A3] mb-6 md:mb-8 px-4">
              Manage your gym, members, and subscriptions all in one place
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/founders-promo"
                className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              >
                Get Started - $20/month
              </Link>
            </div>
            </div>
          </div>
        </section>

        {/* Mobile App Preview Section */}
        <section className="container mx-auto px-4 py-12 md:py-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                See RollOps in Action
              </h2>
              <p className="text-xl text-[#A9A3A3]">Mobile-first design for gym owners on the go</p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Mobile App Screenshots Carousel */}
              <div className="flex justify-center">
                <div className="relative p-1 bg-[#221F1F] rounded-3xl border-4 border-[#504A4A] overflow-hidden shadow-2xl">
                  <img 
                    src={screenshots[currentScreenshotIndex]}
                    alt={`RollOps mobile app screenshot ${currentScreenshotIndex + 1}`}
                    className="w-full h-full object-cover rounded-2xl transition-opacity duration-500"
                    style={{
                      height: '610px',
                      width: '300px',
                      borderRadius: '24px',
                    }}
                  />
                  {/* Screenshot indicators */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                    {screenshots.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentScreenshotIndex(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index === currentScreenshotIndex 
                            ? 'bg-[#9B1421] w-6' 
                            : 'bg-[#504A4A] hover:bg-[#706A6A]'
                        }`}
                        aria-label={`Go to screenshot ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg">
                    <Users className="w-6 h-6 text-text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Member Management</h3>
                    <p className="text-[#A9A3A3]">
                      Easily manage your members, track attendance, and handle enrollments from your phone or computer.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg">
                    <DollarSign className="w-6 h-6 text-text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Automated Billing</h3>
                    <p className="text-[#A9A3A3]">
                      Set up subscriptions, process payments, and track revenue automatically.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg">
                    <Calendar className="w-6 h-6 text-text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Class Scheduling</h3>
                    <p className="text-[#A9A3A3]">
                      Schedule classes, manage instructors, and track attendance in real-time.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="container mx-auto px-4 py-12 md:py-20 bg-[#1A1818]">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 md:mb-12">Features & Benefits</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
            <Link to="/features/member-management" className="bg-[#221F1F] p-6 rounded-lg border border-[#504A4A] hover:border-[#9B1421] transition-colors block">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="w-6 h-6 text-[#EBFFEE] flex-shrink-0" />
                <h3 className="text-xl font-bold">Member Management</h3>
              </div>
              <p className="text-[#A9A3A3]">
                Easily manage your members, track attendance, and handle enrollments.
              </p>
              <span className="text-[#EBFFEE] text-sm mt-2 inline-block hover:underline">Learn more →</span>
            </Link>
            <Link to="/features/billing" className="bg-[#221F1F] p-6 rounded-lg border border-[#504A4A] hover:border-[#9B1421] transition-colors block">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="w-6 h-6 text-[#EBFFEE] flex-shrink-0" />
                <h3 className="text-xl font-bold">Subscription Billing</h3>
              </div>
              <p className="text-[#A9A3A3]">
                Automated billing and payment processing for seamless operations.
              </p>
              <span className="text-[#EBFFEE] text-sm mt-2 inline-block hover:underline">Learn more →</span>
            </Link>
            <Link to="/features/scheduling" className="bg-[#221F1F] p-6 rounded-lg border border-[#504A4A] hover:border-[#9B1421] transition-colors block">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="w-6 h-6 text-[#EBFFEE] flex-shrink-0" />
                <h3 className="text-xl font-bold">Class Scheduling</h3>
              </div>
              <p className="text-[#A9A3A3]">
                Schedule classes, manage instructors, and track attendance.
              </p>
              <span className="text-[#EBFFEE] text-sm mt-2 inline-block hover:underline">Learn more →</span>
            </Link>
            <div className="bg-[#221F1F] p-6 rounded-lg border border-[#504A4A]">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="w-6 h-6 text-[#EBFFEE] flex-shrink-0" />
                <h3 className="text-xl font-bold">Mobile App</h3>
              </div>
              <p className="text-[#A9A3A3]">
                Manage your gym on the go with our iOS and Android apps.
              </p>
            </div>
            <div className="bg-[#221F1F] p-6 rounded-lg border border-[#504A4A]">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="w-6 h-6 text-[#EBFFEE] flex-shrink-0" />
                <h3 className="text-xl font-bold">Real-time Updates</h3>
              </div>
              <p className="text-[#A9A3A3]">
                Get instant notifications and updates about your gym operations.
              </p>
            </div>
            <div className="bg-[#221F1F] p-6 rounded-lg border border-[#504A4A]">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="w-6 h-6 text-[#EBFFEE] flex-shrink-0" />
                <h3 className="text-xl font-bold">Secure & Reliable</h3>
              </div>
              <p className="text-[#A9A3A3]">
                Bank-level security and 99.9% uptime guarantee for your peace of mind.
              </p>
            </div>
          </div>
        </section>

        {/* Book a Demo Section */}
        <section className="container mx-auto px-4 py-12 md:py-20">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-[#9B1421]/20 to-[#7A0F1A]/20 rounded-2xl p-8 md:p-12 border border-[#9B1421]/30">
            <div className="inline-block bg-[#9B1421]/20 p-3 rounded-full mb-4">
              <Video className="w-8 h-8 text-text-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Still Have Questions?
            </h2>
            <p className="text-xl text-[#A9A3A3] mb-2">
              Book a 15-30 minute demo with the Founder/CEO
            </p>
            <p className="text-sm text-[#706A6A] mb-8">
              Limited time offer - Get on the phone immediately and see how RollOps can transform your gym
            </p>
            {GOOGLE_CALENDAR_BOOKING_URL ? (
              <>
                <a
                  href={GOOGLE_CALENDAR_BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-8 py-4 rounded-lg font-semibold text-lg transition-colors mb-4"
                >
                  Book a Demo Now
                </a>
                <p className="text-xs text-[#706A6A]">
                  Opens Google Calendar booking - Select your preferred time slot
                </p>
              </>
            ) : (
              <>
                <a
                  href="mailto:support@rollops.pro?subject=Demo Request - Founders Promo"
                  className="inline-block bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-8 py-4 rounded-lg font-semibold text-lg transition-colors mb-4"
                >
                  Request a Demo
                </a>
                <p className="text-xs text-[#706A6A]">
                  Email us to schedule a 15-30 minute demo with the Founder/CEO
                </p>
              </>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-4 py-12 md:py-20">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-[#9B1421]/20 to-[#7A0F1A]/20 rounded-2xl p-8 md:p-12 border border-[#9B1421]/30">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Transform Your Gym?
            </h2>
            <p className="text-xl text-[#A9A3A3] mb-8">
              Join the Founders Promo and lock in $20/month. Limited spots remaining!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/owner-signup"
                className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              >
                Get Started Now
              </Link>
              <Link
                to="/signin"
                className="bg-[#504A4A] hover:bg-[#5A5454] text-[#EBFFEE] px-8 py-4 rounded-lg font-medium text-lg transition-colors"
              >
                Sign In
              </Link>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="container mx-auto px-4 py-12 md:py-20 bg-[#1A1818]">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">About Us</h2>
            <p className="text-lg text-[#A9A3A3] text-center mb-12">
              Learn about RollOps and our mission to revolutionize gym management
            </p>
            
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold mb-3">Our Mission</h3>
                  <p className="text-[#A9A3A3]">
                    RollOps is designed to help gym owners streamline their operations and focus on what matters most - their members and community. We understand the challenges gym owners face and provide tools to simplify management tasks.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-2xl font-bold mb-3">What We Offer</h3>
                  <p className="text-[#A9A3A3] mb-4">
                    Our all-in-one platform combines member management, billing automation, class scheduling, and real-time analytics in an intuitive interface designed for modern gyms.
                  </p>
                  <ul className="space-y-2 text-[#A9A3A3]">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#9B1421] flex-shrink-0" />
                      Intuitive member and staff management
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#9B1421] flex-shrink-0" />
                      Automated billing and payment processing
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#9B1421] flex-shrink-0" />
                      Flexible class scheduling and attendance tracking
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#9B1421] flex-shrink-0" />
                      Mobile-first design for gym owners on the go
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold mb-3">Our Values</h3>
                  <p className="text-[#A9A3A3] mb-4">
                    We believe in building products that are easy to use, reliable, and focused on solving real problems for gym owners. Your success is our success.
                  </p>
                </div>
              </div>

              <div className="bg-[#221F1F] p-8 rounded-lg border border-[#504A4A] space-y-6">
                <div className="text-center border-b border-[#504A4A] pb-6">
                  <h3 className="text-3xl font-bold text-[#9B1421] mb-2">100%</h3>
                  <p className="text-[#A9A3A3]">Dedicated to your gym's success</p>
                </div>
                
                <div className="text-center border-b border-[#504A4A] pb-6">
                  <h3 className="text-3xl font-bold text-[#9B1421] mb-2">24/7</h3>
                  <p className="text-[#A9A3A3]">System uptime guarantee with support</p>
                </div>

                <div className="text-center border-b border-[#504A4A] pb-6">
                  <h3 className="text-3xl font-bold text-[#9B1421] mb-2">Simple</h3>
                  <p className="text-[#A9A3A3]">Straightforward pricing, no hidden fees</p>
                </div>

                <div className="text-center">
                  <h3 className="text-lg font-bold mb-2">Quick Start</h3>
                  <p className="text-[#A9A3A3] mb-4">
                    Get up and running in minutes, not days
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="container mx-auto px-4 py-12 md:py-20">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Contact Us</h2>
            <p className="text-lg text-[#A9A3A3] text-center mb-12">
              Have questions? We'd love to hear from you. Get in touch with our team.
            </p>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Contact Information */}
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#9B1421]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-1">Email</h3>
                    <p className="text-[#A9A3A3] mb-2">General inquiries and support</p>
                    <a href="mailto:support@rollops.pro" className="text-[#9B1421] hover:text-[#EBFFEE] transition-colors font-semibold">
                      support@rollops.pro
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#9B1421]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-1">Book a Demo</h3>
                    <p className="text-[#A9A3A3] mb-2">Schedule a 15-30 minute demo with our team</p>
                    <a 
                      href={GOOGLE_CALENDAR_BOOKING_URL || "mailto:support@rollops.pro?subject=Demo Request"}
                      target={GOOGLE_CALENDAR_BOOKING_URL ? "_blank" : undefined}
                      rel={GOOGLE_CALENDAR_BOOKING_URL ? "noopener noreferrer" : undefined}
                      className="text-[#9B1421] hover:text-[#EBFFEE] transition-colors font-semibold"
                    >
                      Schedule Now
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#9B1421]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-1">Office</h3>
                    <p className="text-[#A9A3A3]">
                      1908 Thomes Ave STE 12408<br />
                      Cheyenne, WY 82001<br />
                      United States
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Links & Social */}
              <div className="bg-[#221F1F] p-8 rounded-lg border border-[#504A4A] space-y-8">
                <div>
                  <h3 className="text-lg font-bold mb-4">Quick Links</h3>
                  <div className="space-y-3">
                    <a href="/#features" className="block text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors">
                      → Explore Features
                    </a>
                    <a href="/privacy" className="block text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors">
                      → Privacy Policy
                    </a>
                    <a href="/terms" className="block text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors">
                      → Terms of Service
                    </a>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-bold mb-4">Follow Us</h3>
                  <div className="flex gap-4">
                    <a 
                      href="https://www.instagram.com/rollopsgymos" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#9B1421]/20 p-3 rounded-lg hover:bg-[#9B1421]/40 transition-colors"
                    >
                      <svg className="w-5 h-5 text-[#9B1421]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1 1 12.324 0 6.162 6.162 0 0 1-12.324 0zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm4.965-10.322a1.44 1.44 0 1 1 2.881.001 1.44 1.44 0 0 1-2.881-.001z"/>
                      </svg>
                    </a>
                    <a 
                      href="https://www.facebook.com/rollopsgyms" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#9B1421]/20 p-3 rounded-lg hover:bg-[#9B1421]/40 transition-colors"
                    >
                      <Facebook className="w-5 h-5 text-[#9B1421]" />
                    </a>
                    <a 
                      href="https://www.linkedin.com/company/rollops" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#9B1421]/20 p-3 rounded-lg hover:bg-[#9B1421]/40 transition-colors"
                    >
                      <Linkedin className="w-5 h-5 text-[#9B1421]" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="text-center bg-gradient-to-r from-[#9B1421]/20 to-[#7A0F1A]/20 rounded-2xl p-8 border border-[#9B1421]/30">
              <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
              <p className="text-[#A9A3A3] mb-6">
                Join hundreds of gym owners who are already using RollOps to streamline their operations.
              </p>
              <Link
                to="/owner-signup"
                className="inline-block bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-8 py-3 rounded-lg font-semibold transition-colors"
              >
                Start Your Free Trial
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
